/**
 * Companio header behaviour.
 *
 * Ported from the offline prototype. Wrapped in a custom element rather than an
 * IIFE so it re-initialises correctly when the theme editor re-renders the
 * section, and tears its listeners down on removal.
 */

class CompanioHeader extends HTMLElement {
  connectedCallback() {
    this.body = document.body;
    this.header = this.querySelector('[data-companio-header]');
    this.shopMenu = this.querySelector('[data-shop-menu]');
    this.shopToggle = this.querySelector('[data-shop-toggle]');
    this.mobileMenu = this.querySelector('[data-mobile-menu]');
    this.mobileShop = this.querySelector('[data-mobile-shop]');
    this.mobileShopToggle = this.querySelector('[data-mobile-shop-toggle]');
    this.comingModal = this.querySelector('[data-coming-modal]');
    this.toast = this.querySelector('[data-toast]');
    this.toastTimer = null;

    this.onScroll = this.onScroll.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.bind();
    this.onScroll();

    // If the customer form posted, Shopify reloads the page with the modal
    // closed. Re-open it so the success or error message is actually seen.
    if (this.comingModal && this.comingModal.querySelector('.form-message')) {
      this.openComingModal(null);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('keydown', this.onKeydown);
    this.body.classList.remove('modal-open');
  }

  bind() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('keydown', this.onKeydown);

    if (this.shopToggle) {
      this.shopToggle.addEventListener('click', (event) => {
        event.stopPropagation();
        const open = this.shopMenu.classList.toggle('open');
        this.shopToggle.setAttribute('aria-expanded', String(open));
      });
    }

    const menuOpen = this.querySelector('[data-menu-open]');
    const menuClose = this.querySelector('[data-menu-close]');
    if (menuOpen) menuOpen.addEventListener('click', () => this.openDrawer());
    if (menuClose) menuClose.addEventListener('click', () => this.closeDrawer());

    if (this.mobileMenu) {
      this.mobileMenu.addEventListener('click', (event) => {
        if (event.target === this.mobileMenu) this.closeDrawer();
      });
    }

    if (this.mobileShopToggle) {
      this.mobileShopToggle.addEventListener('click', () => {
        const open = this.mobileShop.classList.toggle('open');
        this.mobileShopToggle.setAttribute('aria-expanded', String(open));
      });
    }

    this.querySelectorAll('[data-coming]').forEach((button) => {
      button.addEventListener('click', () => this.openComingModal(button.dataset.coming));
    });

    const comingClose = this.querySelector('[data-coming-close]');
    if (comingClose) comingClose.addEventListener('click', () => this.closeComingModal());

    if (this.comingModal) {
      this.comingModal.addEventListener('click', (event) => {
        if (event.target === this.comingModal) this.closeComingModal();
      });
    }
  }

  onScroll() {
    if (!this.header) return;
    this.header.classList.toggle('scrolled', window.scrollY > 24);
  }

  onDocumentClick(event) {
    if (this.shopMenu && !this.shopMenu.contains(event.target)) this.closeShopMenu();
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    this.closeShopMenu();
    this.closeComingModal();
    this.closeDrawer();
  }

  closeShopMenu() {
    if (!this.shopMenu) return;
    this.shopMenu.classList.remove('open');
    if (this.shopToggle) this.shopToggle.setAttribute('aria-expanded', 'false');
  }

  openDrawer() {
    if (!this.mobileMenu) return;
    this.mobileMenu.classList.add('open');
    this.mobileMenu.setAttribute('aria-hidden', 'false');
    this.body.classList.add('modal-open');
  }

  closeDrawer() {
    if (!this.mobileMenu) return;
    this.mobileMenu.classList.remove('open');
    this.mobileMenu.setAttribute('aria-hidden', 'true');
    this.syncBodyLock();
  }

  openComingModal(productName) {
    if (!this.comingModal) return;

    if (productName) {
      const label = this.comingModal.querySelector('[data-coming-product]');
      const tag = this.comingModal.querySelector('[data-coming-tag]');
      if (label) label.textContent = productName;
      // Tag the customer with the product they asked about, so the waitlist is
      // segmentable in the Shopify admin.
      if (tag) tag.value = 'coming-soon,' + productName.toLowerCase().replace(/\s+/g, '-');
    }

    this.comingModal.classList.add('open');
    this.comingModal.setAttribute('aria-hidden', 'false');
    this.body.classList.add('modal-open');
    this.closeShopMenu();
  }

  closeComingModal() {
    if (!this.comingModal) return;
    this.comingModal.classList.remove('open');
    this.comingModal.setAttribute('aria-hidden', 'true');
    this.syncBodyLock();
  }

  syncBodyLock() {
    const drawerOpen = this.mobileMenu && this.mobileMenu.classList.contains('open');
    const modalOpen = this.comingModal && this.comingModal.classList.contains('open');
    if (!drawerOpen && !modalOpen) this.body.classList.remove('modal-open');
  }

  showToast(message) {
    if (!this.toast) return;
    this.toast.textContent = message;
    this.toast.classList.add('show');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.toast.classList.remove('show'), 1800);
  }
}

if (!customElements.get('companio-header')) {
  customElements.define('companio-header', CompanioHeader);
}
